(() => {
  'use strict';
  if (customElements.get('collections-spotlight')) return;

  customElements.define('collections-spotlight', class extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('[data-section-type="collections-spotlight"]');
      this.frame = this.parentElement;
      this.tabs = [...this.querySelectorAll('[data-hover-target]')];
      this.panels = [...this.querySelectorAll('[data-collection-image]')];
      this.active = -1;
      this.events = new AbortController();
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      const signal = this.events.signal;
      this.tabs.forEach((tab, index) => {
        ['mouseenter', 'touchstart', 'focus', 'click'].forEach(type => {
          tab.addEventListener(type, event => this.choose(event, index), { signal });
        });
        tab.addEventListener('keydown', event => this.onKey(event, index), { signal });
      });
      this.select(0);
      if (this.section.dataset.scrollStory !== 'true' || this.tabs.length < 2) return;
      window.addEventListener('scroll', () => this.queueRender(), { passive: true, signal });
      window.addEventListener('resize', () => this.configure(), { passive: true, signal });
      this.motion.addEventListener('change', () => this.configure(), { signal });
      this.configure();
      document.fonts?.ready.then(() => { if (this.isConnected) this.configure(); });
    }

    disconnectedCallback() {
      this.events?.abort();
      cancelAnimationFrame(this.raf);
      this.section?.classList.remove('spotlight--scroll-story');
    }

    configure() {
      // Short viewports and reduced motion retain the normal, fully readable section.
      const enabled = !this.motion.matches && !window.Shopify?.designMode && innerHeight >= 620;
      this.enabled = enabled;
      this.section.classList.toggle('spotlight--scroll-story', enabled);
      const header = document.querySelector('#SiteHeader');
      const top = Math.min(90, Math.max(0, header?.getBoundingClientRect().height || 64));
      this.top = top;
      this.viewHeight = innerHeight - top;
      this.step = Math.max(240, Math.min(560, this.viewHeight * 0.65));
      this.section.style.setProperty('--story-top', `${top}px`);
      this.section.style.setProperty('--story-view', `${this.viewHeight}px`);
      this.section.style.setProperty('--story-height', `${this.viewHeight + this.tabs.length * this.step}px`);
      const list = this.querySelector('.spotlight__links');
      if (enabled) {
        list.setAttribute('role', 'tablist');
        list.setAttribute('aria-orientation', 'vertical');
        list.setAttribute('aria-label', this.querySelector('h2')?.textContent.trim() || 'Features');
      } else {
        ['role', 'aria-orientation', 'aria-label'].forEach(name => list.removeAttribute(name));
      }
      this.tabs.forEach((tab, index) => {
        const panel = this.panels.find(item => item.id === tab.dataset.hoverTarget);
        if (enabled) {
          tab.id ||= `${tab.dataset.hoverTarget}-tab`;
          tab.setAttribute('role', 'tab');
          tab.setAttribute('aria-controls', panel.id);
          panel.setAttribute('role', 'tabpanel');
          panel.setAttribute('aria-labelledby', tab.id);
        } else {
          ['role', 'aria-controls', 'aria-selected', 'tabindex'].forEach(name => tab.removeAttribute(name));
          ['role', 'aria-labelledby', 'aria-hidden'].forEach(name => panel.removeAttribute(name));
        }
      });
      this.select(this.active < 0 ? 0 : this.active, true);
      this.queueRender();
    }

    choose(event, index) {
      if (this.enabled) {
        // Hover must not undo the scroll position when the pointer rests on a title.
        if (event.type !== 'click') return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.goTo(index);
        return;
      }
      if (['click', 'touchstart'].includes(event.type) && window.theme?.isMobile()) event.preventDefault();
      this.select(index);
    }

    onKey(event, index) {
      if (!this.enabled) return;
      const keys = { ArrowDown: Math.min(index + 1, this.tabs.length - 1), ArrowUp: Math.max(index - 1, 0), Home: 0, End: this.tabs.length - 1 };
      if (!(event.key in keys) && event.key !== ' ') return;
      event.preventDefault();
      const next = event.key === ' ' ? index : keys[event.key];
      this.goTo(next);
      this.tabs[next].focus({ preventScroll: true });
    }

    goTo(index) {
      const start = this.section.getBoundingClientRect().top + scrollY - this.top;
      this.select(index);
      // Select the middle of a step, avoiding rounding at a stage boundary.
      window.scrollTo({ top: start + (index + 0.2) * this.step, behavior: 'instant' });
    }

    queueRender() {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = 0;
        if (!this.enabled) return;
        const progress = this.top - this.section.getBoundingClientRect().top;
        const index = Math.min(this.tabs.length - 1, Math.max(0, Math.floor(progress / this.step)));
        this.select(index);
      });
    }

    select(index, force = false) {
      if (!force && this.active === index) return;
      this.active = index;
      const current = this.tabs[index];
      if (!current) return;
      this.tabs.forEach((tab, i) => {
        tab.classList.toggle('is-selected', i === index);
        if (this.enabled) {
          tab.setAttribute('aria-selected', String(i === index));
          tab.tabIndex = i === index ? 0 : -1;
        }
      });
      this.panels.forEach(panel => {
        const visible = panel.id === current.dataset.hoverTarget;
        panel.classList.toggle('is-visible', visible);
        if (this.enabled) panel.setAttribute('aria-hidden', String(!visible));
      });
      const background = current.closest('[data-bg]')?.dataset.bg;
      if (background && !['#00000000', 'rgba(0,0,0,0)', 'transparent'].includes(background)) {
        this.section.style.setProperty('--bg', background);
      } else {
        this.section.style.removeProperty('--bg');
      }
    }
  });
})();
